import React from 'react';
import { i18n } from 'config/locales';

import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { DismissalModel } from 'forms/sections/history/employment/types';
import { CommonOnError, CommonRequired, CommonScrollIntoView } from 'types/props';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Field from 'components/Form/Field';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import { employmentDimissalTypes } from 'constants/enums/employmentOptions';
import { Summary, TelephoneSummary } from 'components/Summary';
import PVQName from '../PVQName';
import Branch from '../Branch';
import EmailCollection from '../PVQEmail/EmailCollection';
import TelephoneCollection from '../Telephone/TelephoneCollection';
import NotApplicable from '../NotApplicable';

interface Props
  extends ValidationElementProps,
  Partial<DismissalModel>,
  Omit<AccordionItemProps, 'name'> {
  onUpdate: (values:Partial<DismissalModel>, dismissalType: string) => void
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  attribute?: string
  dismissalType: string
  errors?:any
  }

export default class DismissalItem extends ValidationElement<Props> {
  update = (queue: Partial<DismissalModel>) => {
    this.props.onUpdate({
      Description: this.props.Description,
      Date: this.props.Date,
      GivenBy: this.props.GivenBy,
      GivenByTelephone: this.props.GivenByTelephone,
      GivenByEmail: this.props.GivenByEmail,
      InPerson: this.props.InPerson,
      InWriting: this.props.InWriting,
      ...queue,
    }, this.props.dismissalType);
  };

  updateDescription = (values: DismissalModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateDate = (values: DismissalModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateGivenBy = (values: DismissalModel['GivenBy']) => {
    this.update({
      GivenBy: values,
    });
  };

  updateGivenByTelephone = (values: DismissalModel['GivenByTelephone']) => {
    this.update({
      GivenByTelephone: values,
    });
  };

  updateGivenByEmail = (values: DismissalModel['GivenByEmail']) => {
    this.update({
      GivenByEmail: values,
    });
  };

  updateInWriting = (values: DismissalModel['InWriting']) => {
    this.update({
      InWriting: values,
    });
  };

  updateInPerson = (values: DismissalModel['InPerson']) => {
    this.update({
      InPerson: values,
    });
  };

  updatePhoneNumbers = (values: DismissalModel['GivenByTelephone']) => {
    this.update({
      GivenByTelephone: values,
    });
  };

  updateEmails = (values: DismissalModel['GivenByEmail']) => {
    this.update({
      GivenByEmail: values,
    });
  };

  updateDateNotApplicable = (values: DismissalModel['DateNotApplicable']) => {
    this.update({
      Date: values?.applicable ? this.props.Date : {},
      DateNotApplicable: values,
    });
  };

  /**
   * Assists in rendering the summary section.
   */
  phoneNumberSummary = (item, index) => {
    const number = TelephoneSummary((item.Item || {}).Telephone);
    return Summary({
      type: i18n.t('identification.contacts.collection.summary.phoneNumber'),
      index,
      left: number,
      right: null,
      placeholder: i18n.t('identification.contacts.collection.summary.unknownPhone'),
    });
  };

  emailSummary = (item, index) => {
    const itemProperties = (item || {}).Item || {};
    const email = itemProperties.Email && itemProperties.Email.value ? itemProperties.Email.value : '';
    return Summary({
      type: i18n.t('identification.pvq.contacts.collection.summary.email'),
      index,
      left: email,
      right: null,
      placeholder: i18n.t('identification.pvq.contacts.collection.summary.unknownEmail'),
    });
  };

  firstItemRequired = (item, index, initial, callback) => {
    if (index === 0) {
      return <span className="required">{callback()}</span>;
    }
    return callback();
  };

  render() {
    const pvqPrefix = `history.pvq.employment.default.departure.${this.props.dismissalType}`;

    return (
      <div>
        {this.props.dismissalType === employmentDimissalTypes.fired && (
        <Field
          attribute={setAttribute(this.props.attribute, `Date`)}
          title={i18n.t(`${pvqPrefix}.date.label`)}
          titleSize="h4"
          className="date-left"
          adjustFor="labels"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            name="DateNotApplicable"
            {...this.props.DateNotApplicable}
            label={i18n.t('branch.label.dnk')}
            or={i18n.t('branch.label.or')}
            onUpdate={this.updateDateNotApplicable}
            attribute="DateNotApplicable"
          >
            <DateControl
              attribute={setAttribute(this.props.attribute, `Date`)}
              name="Date"
              {...this.props.Date}
              onUpdate={this.updateDate}
              minDateEqualTo
              maxDate={{ year: new Date().getFullYear() }}
              onError={this.props.onError}
              required={this.props.required}
              hideDay
            />
          </NotApplicable>
        </Field>
        )}
        <Field
          attribute={setAttribute(this.props.attribute, `Description`)}
          title={i18n.t(`${pvqPrefix}.description.label`)}
          titleSize="h4"
          className="explanation-left"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            attribute={setAttribute(this.props.attribute, `Description`)}
            name="Description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            maxlength="100"
            required={this.props.required}
          />
        </Field>
        <Field
          attribute={setAttribute(this.props.attribute, `InWriting`)}
          className="explanation-left"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Branch
            attribute={setAttribute(this.props.attribute, `InWriting`)}
            name="has_affiliation"
            label={i18n.t(`${pvqPrefix}.inWriting.label`)}
            labelSize="h4"
            className="relative-affiliation"
            {...this.props.InWriting}
            onUpdate={this.updateInWriting}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
        </Field>
        <Field
          attribute={setAttribute(this.props.attribute, `InPerson`)}
          className="explanation-left"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Branch
            attribute={setAttribute(this.props.attribute, `InPerson`)}
            name="has_affiliation"
            label={i18n.t(`${pvqPrefix}.inPerson.label`)}
            labelSize="h4"
            className="relative-affiliation"
            {...this.props.InPerson}
            onUpdate={this.updateInPerson}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            onError={this.props.onError}
          />
        </Field>
        <Field
          attribute={setAttribute(this.props.attribute, `GivenBy`)}
          title={i18n.t(`${pvqPrefix}.givenBy.label`)}
          titleSize="h4"
          className="explanation-left"
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          exactAttribute
        >
          <PVQName
            attribute={setAttribute(this.props.attribute, `GivenBy`)}
            exactAttribute
            aria-label="Provide full name"
            name="name"
            {...this.props.GivenBy}
            onUpdate={this.updateGivenBy}
            onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            hideMiddleName
            showLastFirstMiddleNameOrder
            hideSuffix
          />

        </Field>
        <TelephoneCollection
          onUpdate={this.updatePhoneNumbers}
          onError={this.props.onError}
          required={this.props.required}
          attribute={setAttribute(this.props.attribute, `GivenByTelephone`)}
          telephoneFieldTitle={i18n.t('history.pvq.employment.default.departure.phoneNumbers.title')}
          appendFieldTitle={i18n.t('history.pvq.employment.default.departure.phoneNumbers.append')}
          branchFieldTitle={i18n.t('history.pvq.employment.default.departure.phoneNumbers.branch.title')}
          allowNotApplicable
          hideInitialBranchQuestion
          showTimeOfDay
          timeOfDayLabel={i18n.t('history.pvq.employment.default.departure.phoneNumbers.timeOfDay')}
          {...this.props.GivenByTelephone}
        />
        <EmailCollection
          onUpdate={this.updateEmails}
          onError={this.props.onError}
          required={this.props.required}
          attribute={setAttribute(this.props.attribute, `GivenByEmail`)}
          emailFieldTitle={i18n.t('history.pvq.employment.default.departure.emails.title')}
          appendFieldTitle={i18n.t('history.pvq.employment.default.departure.emails.append')}
          branchFieldTitle={i18n.t('history.pvq.employment.default.departure.emails.branch.title')}
          {...this.props.GivenByEmail}
          showEmailUnknown
          includeEmailType={false}
        />
      </div>
    );
  }
}

DismissalItem.defaultProps = {
  dismissalType: 'default',
  Date: { value: '' },
  Description: { value: '' },
  InWriting: { value: '' },
  InPerson: { value: '' },
  GivenBy: { value: '' },
  GivenByTelephone: { },
  GivenByEmail: { },
  isPVQ: true,
  formType: '',
  onUpdate: (queue, dismissalType) => { },
  onError: (value, arr) => arr,
};
